import { useState, useEffect } from "react";
import {
  Modal,
  Text,
  Button,
  Stack,
  Checkbox,
  Group,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useDeleteTranslation } from "../../api/translations";

function DeleteTranslationModal({
  opened,
  onClose,
  record,
  validLanguages = [],
}) {
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const { mutate: deleteTranslations, isPending } = useDeleteTranslation();

  useEffect(() => {
    if (opened) {
      setSelectedLanguages([]);
    }
  }, [opened]);

  const handleLanguageToggle = (langCode) => {
    setSelectedLanguages((prev) =>
      prev.includes(langCode)
        ? prev.filter((code) => code !== langCode)
        : [...prev, langCode]
    );
  };

  const handleSelectAll = () => {
    if (selectedLanguages.length === deletableLanguages.length) {
      setSelectedLanguages([]);
    } else {
      setSelectedLanguages([...deletableLanguages]);
    }
  };

  const handleDelete = () => {
    if (selectedLanguages.length === 0) return;

    const idsToDelete = selectedLanguages
      .map((langCode) => record.ids[langCode])
      .filter(Boolean);

    if (idsToDelete.length === 0) {
      return;
    }

    deleteTranslations(
      { ids: idsToDelete },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    if (isPending) return;
    setSelectedLanguages([]);
    onClose();
  };

  if (!record) return null;

  const deletableLanguages = Object.keys(record.ids || {}).filter(
    (lang) => record.ids[lang]
  );

  const inactiveLanguages = deletableLanguages.filter(
    (lang) => !validLanguages.includes(lang)
  );
  const displayLanguages = [...validLanguages, ...inactiveLanguages];

  const allSelected =
    deletableLanguages.length > 0 &&
    selectedLanguages.length === deletableLanguages.length;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Delete translation?"
      size="md"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <Stack gap="md">
        <Text size="sm">
          Select which language variants you want to delete for the key{" "}
          <strong>"{record.translationKey}"</strong>:
        </Text>

        {deletableLanguages.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            No translations available to delete.
          </Alert>
        ) : (
          <>
            <Stack gap="xs">
              <Checkbox
                label="Select all languages"
                checked={allSelected}
                onChange={handleSelectAll}
                fw={500}
                disabled={isPending}
              />

              {displayLanguages.map((langCode) => {
                const hasRow = !!record.ids[langCode];
                const isInactive = !validLanguages.includes(langCode);
                const value = record.languages[langCode];

                return (
                  <Checkbox
                    key={langCode}
                    label={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {langCode.toUpperCase()}
                        </Text>
                        {isInactive && (
                          <Text size="sm" c="orange">
                            (disabled language)
                          </Text>
                        )}
                        <Text size="sm" c="dimmed">
                          {hasRow
                            ? value
                              ? `"${value}"`
                              : "(empty)"
                            : "(no translation)"}
                        </Text>
                      </Group>
                    }
                    checked={selectedLanguages.includes(langCode)}
                    onChange={() => handleLanguageToggle(langCode)}
                    disabled={!hasRow || isPending}
                    ml="md"
                  />
                );
              })}
            </Stack>

            {selectedLanguages.length === deletableLanguages.length && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                Warning: You are about to delete all language variants. The
                translation key will be completely removed.
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            disabled={selectedLanguages.length === 0}
            loading={isPending}
          >
            Delete{" "}
            {selectedLanguages.length > 0 && `(${selectedLanguages.length})`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default DeleteTranslationModal;
